import { Directive, effect, ElementRef, input } from '@angular/core';
import katex from 'katex';

import { interpolateValues } from './interpolate-values';

/**
 * Renders rich text with LaTeX (`$...$`), value interpolation (`{path}`),
 * and line breaks (`\n`) directly into the host element's DOM.
 *
 * Uses `katex.render()` instead of `innerHTML` so SVG elements (e.g. \sqrt)
 * are created natively in the DOM and never pass through Angular's sanitizer.
 *
 * Usage:
 *   <span [dojoRichText]="'$\\frac{\\sqrt{3}}{2}$'"></span>
 *   <td [dojoRichText]="value" [richTextResolve]="resolveFn"></td>
 */
@Directive({
  selector: '[dojoRichText]',
  standalone: true,
})
export class RichTextDirective {
  constructor(private readonly elementRef: ElementRef<HTMLElement>) {
    effect(() => {
      const value = this.dojoRichText();
      const resolveFn = this.richTextResolve();
      const prec = this.richTextPrecision();
      this.render(value, resolveFn, prec);
    });
  }

  readonly dojoRichText = input.required<string | number | null | undefined>();
  readonly richTextResolve = input<((path: string) => number | string | undefined) | undefined>(undefined);
  readonly richTextPrecision = input<number | undefined>(undefined);

  private render(
    value: string | number | null | undefined,
    resolveFn?: (path: string) => number | string | undefined,
    precision?: number,
  ): void {
    const element = this.elementRef.nativeElement;
    element.textContent = '';

    if (value === null || value === undefined) {
      return;
    }

    let text = String(value);

    if (resolveFn) {
      text = interpolateValues(text, resolveFn, precision);
    }

    // If no LaTeX delimiters, just handle line breaks
    if (!text.includes('$')) {
      element.innerHTML = text.replace(/\n/g, '<br>');
      return;
    }

    // Split into plain text (even indices) and LaTeX (odd indices)
    const parts = text.split(/(?<!\\)\$(.+?)(?<!\\)\$/g);

    for (let i = 0; i < parts.length; i++) {
      if (i % 2 === 0) {
        // Plain text segment — handle line breaks
        if (!parts[i]) {
          continue;
        }
        const lines = parts[i].split('\n');
        lines.forEach((line, j) => {
          if (j > 0) {
            element.appendChild(document.createElement('br'));
          }
          if (line) {
            element.appendChild(document.createTextNode(line));
          }
        });
      } else {
        // LaTeX segment — render directly into a span via katex.render()
        const span = document.createElement('span');
        katex.render(parts[i], span, { throwOnError: false, output: 'html' });
        element.appendChild(span);
      }
    }
  }
}
